import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { 
  Shirt, Package, Lock, Check, X, Loader2, 
  Upload, Image, AlertTriangle, ShoppingBag, ExternalLink, Globe, Eye, Sparkles
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const OrgMerchandise = ({ organization }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({
    platform_connected: false,
    tier: "free",
    tier_has_access: false,
    merchandise_enabled: false,
    public_shop_enabled: false,
    design_url: null,
    can_enable: false
  });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Mockup preview state
  const [mockupDialogOpen, setMockupDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [mockupLoading, setMockupLoading] = useState(false);
  const [mockupImages, setMockupImages] = useState([]);
  const [mockupTaskKey, setMockupTaskKey] = useState(null);
  
  // Product customization state
  const [productCustomizations, setProductCustomizations] = useState([]);
  const [customizeDialogOpen, setCustomizeDialogOpen] = useState(false);
  const [customizingProduct, setCustomizingProduct] = useState(null);
  const [customizeSaving, setCustomizeSaving] = useState(false);

  useEffect(() => {
    loadStatus();
    loadProducts();
    loadOrders();
    loadProductCustomizations();
  }, [organization?.org_id]);

  const loadStatus = async () => {
    try {
      const response = await axios.get(`${API}/printful/org/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Error loading merchandise status:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    if (!organization?.org_id) return;
    try {
      const response = await axios.get(`${API}/printful/merchandise/${organization.org_id}`);
      setProducts(response.data.merchandise || []);
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };
  
  const loadProductCustomizations = async () => {
    try {
      const response = await axios.get(`${API}/printful/org/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductCustomizations(response.data.products || []);
    } catch (error) {
      console.error("Error loading product customizations:", error);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/printful/org/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error loading orders:", error);
    }
  };

  const toggleMerchandise = async (enabled) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/printful/org/settings`,
        { 
          merchandise_enabled: enabled, 
          design_url: status.design_url,
          public_shop_enabled: enabled ? status.public_shop_enabled : false
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(prev => ({ 
        ...prev, 
        merchandise_enabled: enabled,
        public_shop_enabled: enabled ? prev.public_shop_enabled : false
      }));
      toast.success(enabled ? "Merchandise enabled!" : "Merchandise disabled");
      if (enabled) loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const togglePublicShop = async (enabled) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/printful/org/settings`,
        { 
          merchandise_enabled: status.merchandise_enabled, 
          design_url: status.design_url,
          public_shop_enabled: enabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(prev => ({ ...prev, public_shop_enabled: enabled }));
      toast.success(enabled ? "Public shop enabled!" : "Public shop disabled");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDesignUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a PNG, JPEG, or SVG file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be smaller than 10MB");
      return;
    }

    setUploading(true);
    try {
      // Upload using direct upload endpoint
      const formData = new FormData();
      formData.append("file", file);
      formData.append("image_type", "merchandise");
      formData.append("entity_id", organization.org_id);

      const uploadRes = await axios.post(`${API}/upload/direct`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      const designUrl = uploadRes.data.url;

      // Update org settings
      await axios.put(
        `${API}/printful/org/settings`,
        { 
          merchandise_enabled: status.merchandise_enabled,
          design_url: designUrl 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStatus(prev => ({ ...prev, design_url: designUrl }));
      toast.success("Design uploaded successfully!");
    } catch (error) {
      toast.error("Failed to upload design");
    } finally {
      setUploading(false);
    }
  };

  // Generate mockup preview
  const generateMockup = async (product) => {
    if (!status.design_url) {
      toast.error("Please upload a design first");
      return;
    }
    
    setSelectedProduct(product);
    setMockupDialogOpen(true);
    setMockupLoading(true);
    setMockupImages([]);
    
    try {
      // Start mockup generation
      const response = await axios.post(
        `${API}/printful/mockup/generate`,
        {
          product_id: product.printful_product_id || product.product_id,
          design_url: status.design_url,
          placement: "front"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const taskKey = response.data.task_key;
      setMockupTaskKey(taskKey);
      
      // Poll for results
      pollMockupStatus(taskKey);
    } catch (error) {
      console.error("Mockup generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate mockup");
      setMockupLoading(false);
    }
  };
  
  // Poll mockup status
  const pollMockupStatus = async (taskKey, attempts = 0) => {
    if (attempts > 30) { // Max 30 attempts (30 seconds)
      toast.error("Mockup generation timed out");
      setMockupLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/printful/mockup/status/${taskKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "completed") {
        setMockupImages(response.data.mockups || []);
        setMockupLoading(false);
      } else if (response.data.status === "failed") {
        toast.error("Mockup generation failed");
        setMockupLoading(false);
      } else {
        // Still pending, poll again
        setTimeout(() => pollMockupStatus(taskKey, attempts + 1), 1000);
      }
    } catch (error) {
      console.error("Error checking mockup status:", error);
      setMockupLoading(false);
    }
  };

  // Open customization dialog for a product
  const openCustomizeDialog = (productData) => {
    const { product, customization } = productData;
    
    // If no customization exists, default to only "White" selected (if available)
    let initialColors = customization?.selected_colors;
    if (initialColors === undefined || initialColors === null) {
      const availableColors = product.colors_available || [];
      const hasWhite = availableColors.includes("White");
      // Default to just White if available, otherwise first color
      if (hasWhite) {
        initialColors = ["White"];
      } else if (availableColors.length > 0) {
        initialColors = [availableColors[0]];
      } else {
        initialColors = null;
      }
    }
    
    setCustomizingProduct({
      ...product,
      customization: {
        enabled: customization?.enabled !== false,
        selected_colors: initialColors,
        selected_sizes: customization?.selected_sizes || null,
      }
    });
    setCustomizeDialogOpen(true);
  };

  // Select/Deselect all colors
  const selectAllColors = () => {
    if (!customizingProduct) return;
    setCustomizingProduct({
      ...customizingProduct,
      customization: {
        ...customizingProduct.customization,
        selected_colors: null // null means all selected
      }
    });
  };

  const deselectAllColors = () => {
    if (!customizingProduct) return;
    setCustomizingProduct({
      ...customizingProduct,
      customization: {
        ...customizingProduct.customization,
        selected_colors: [] // empty array means none selected
      }
    });
  };

  // Select/Deselect all sizes
  const selectAllSizes = () => {
    if (!customizingProduct) return;
    setCustomizingProduct({
      ...customizingProduct,
      customization: {
        ...customizingProduct.customization,
        selected_sizes: null // null means all selected
      }
    });
  };

  const deselectAllSizes = () => {
    if (!customizingProduct) return;
    setCustomizingProduct({
      ...customizingProduct,
      customization: {
        ...customizingProduct.customization,
        selected_sizes: [] // empty array means none selected
      }
    });
  };

  // Save product customization
  const saveProductCustomization = async () => {
    if (!customizingProduct) return;
    
    setCustomizeSaving(true);
    try {
      await axios.put(
        `${API}/printful/org/products/${customizingProduct.product_id}`,
        {
          product_id: customizingProduct.product_id,
          enabled: customizingProduct.customization.enabled,
          selected_colors: customizingProduct.customization.selected_colors,
          selected_sizes: customizingProduct.customization.selected_sizes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Product settings saved!");
      setCustomizeDialogOpen(false);
      loadProductCustomizations();
      loadProducts(); // Refresh the public product list too
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setCustomizeSaving(false);
    }
  };

  // Toggle a color in the customization
  const toggleColor = (color) => {
    if (!customizingProduct) return;
    
    const currentColors = customizingProduct.customization.selected_colors;
    const allColors = customizingProduct.colors_available || [];
    
    let newColors;
    if (currentColors === null) {
      // Currently showing all - switching to selection mode, deselect this one
      newColors = allColors.filter(c => c !== color);
    } else if (currentColors.includes(color)) {
      // Remove the color
      newColors = currentColors.filter(c => c !== color);
      // If all are selected again, set to null (all)
      if (newColors.length === allColors.length) {
        newColors = null;
      }
    } else {
      // Add the color
      newColors = [...currentColors, color];
      // If all are selected, set to null (all)
      if (newColors.length === allColors.length) {
        newColors = null;
      }
    }
    
    setCustomizingProduct({
      ...customizingProduct,
      customization: {
        ...customizingProduct.customization,
        selected_colors: newColors
      }
    });
  };

  // Toggle a size in the customization
  const toggleSize = (size) => {
    if (!customizingProduct) return;
    
    const currentSizes = customizingProduct.customization.selected_sizes;
    const allSizes = customizingProduct.sizes_available || [];
    
    let newSizes;
    if (currentSizes === null) {
      // Currently showing all - switching to selection mode, deselect this one
      newSizes = allSizes.filter(s => s !== size);
    } else if (currentSizes.includes(size)) {
      // Remove the size
      newSizes = currentSizes.filter(s => s !== size);
      // If all are selected again, set to null (all)
      if (newSizes.length === allSizes.length) {
        newSizes = null;
      }
    } else {
      // Add the size
      newSizes = [...currentSizes, size];
      // If all are selected, set to null (all)
      if (newSizes.length === allSizes.length) {
        newSizes = null;
      }
    }
    
    setCustomizingProduct({
      ...customizingProduct,
      customization: {
        ...customizingProduct.customization,
        selected_sizes: newSizes
      }
    });
  };

  // Check if a color is selected
  const isColorSelected = (color) => {
    if (!customizingProduct) return false;
    const selected = customizingProduct.customization.selected_colors;
    return selected === null || selected.includes(color);
  };

  // Check if a size is selected
  const isSizeSelected = (size) => {
    if (!customizingProduct) return false;
    const selected = customizingProduct.customization.selected_sizes;
    return selected === null || selected.includes(size);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Tier doesn't have access
  if (!status.tier_has_access) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Merchandise Feature Locked
            </h3>
            <p className="text-slate-500 max-w-md mx-auto mb-6">
              Merchandise sales require a Professional or Enterprise subscription tier.
              Currently on: <span className="font-medium capitalize">{status.tier}</span> tier
            </p>
            <Badge variant="outline" className="text-amber-800 border-amber-200 bg-amber-50">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Upgrade Required
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Platform not connected
  if (!status.platform_connected) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shirt className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Merchandise Not Available
            </h3>
            <p className="text-slate-500 max-w-md mx-auto">
              Platform merchandise is not currently available. 
              Please contact the platform administrator.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${status.merchandise_enabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                <ShoppingBag className={`w-5 h-5 ${status.merchandise_enabled ? 'text-green-600' : 'text-slate-500'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">Team Merchandise</CardTitle>
                <CardDescription>
                  Allow parents to purchase branded team gear during registration
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={status.merchandise_enabled}
              onCheckedChange={toggleMerchandise}
              disabled={!status.can_enable || saving}
              data-testid="merchandise-toggle"
            />
          </div>
        </CardHeader>
      </Card>

      {/* Public Shop Toggle */}
      {status.merchandise_enabled && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${status.public_shop_enabled ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                  <Globe className={`w-5 h-5 ${status.public_shop_enabled ? 'text-indigo-600' : 'text-slate-500'}`} />
                </div>
                <div>
                  <CardTitle className="text-lg">Public Shop</CardTitle>
                  <CardDescription>
                    Enable a public merchandise shop accessible from your organization&apos;s page
                  </CardDescription>
                </div>
              </div>
              <Switch
                checked={status.public_shop_enabled}
                onCheckedChange={togglePublicShop}
                disabled={saving}
                data-testid="public-shop-toggle"
              />
            </div>
          </CardHeader>
          {status.public_shop_enabled && organization?.subdomain && (
            <CardContent className="pt-0">
              <a 
                href={`/org/${organization.subdomain}/shop`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
              >
                <ExternalLink className="w-4 h-4" />
                View your public shop
              </a>
            </CardContent>
          )}
        </Card>
      )}

      {/* Design Upload */}
      {status.merchandise_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Image className="w-5 h-5" />
              Organization Logo/Design
            </CardTitle>
            <CardDescription>
              Upload your organization&apos;s logo to be printed on merchandise
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              {/* Preview */}
              <div className="w-32 h-32 bg-slate-100 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-300 overflow-hidden">
                {status.design_url ? (
                  <img 
                    src={status.design_url} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Shirt className="w-12 h-12 text-slate-300" />
                )}
              </div>

              {/* Upload */}
              <div className="flex-1">
                <p className="text-sm text-slate-600 mb-3">
                  For best results, upload a high-resolution PNG with transparent background.
                  Recommended: 300 DPI, at least 2000px wide.
                </p>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    disabled={uploading}
                    onClick={() => document.getElementById('design-upload').click()}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Design
                      </>
                    )}
                  </Button>
                  <input
                    id="design-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    className="hidden"
                    onChange={handleDesignUpload}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  PNG, JPEG, or SVG. Max 10MB.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Products - Customization */}
      {status.merchandise_enabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5" />
              Product Settings
            </CardTitle>
            <CardDescription>
              Customize which products, colors, and sizes are available for your organization.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {productCustomizations.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Shirt className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No products available</p>
                <p className="text-sm">Check back later for merchandise options</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {productCustomizations.map((item) => {
                  const product = item.product;
                  const customization = item.customization;
                  const isEnabled = customization?.enabled !== false;
                  const selectedColors = customization?.selected_colors;
                  const selectedSizes = customization?.selected_sizes;
                  
                  return (
                    <div 
                      key={product.product_id}
                      className={`border rounded-xl p-4 transition-all ${
                        isEnabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'
                      }`}
                    >
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-32 object-contain mb-3 bg-slate-50 rounded-lg"
                        />
                      ) : (
                        <div className="w-full h-32 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                          <Shirt className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-900">{product.name}</h4>
                        <Badge variant={isEnabled ? "default" : "secondary"}>
                          {isEnabled ? "Active" : "Disabled"}
                        </Badge>
                      </div>
                      <p className="text-lg font-bold text-slate-900">
                        ${product.retail_price?.toFixed(2)}
                      </p>
                      
                      {/* Show current selections summary */}
                      <div className="mt-2 space-y-1 text-xs text-slate-500">
                        <p>
                          Colors: {selectedColors ? `${selectedColors.length} of ${product.colors_available?.length || 0}` : 'All'}
                        </p>
                        <p>
                          Sizes: {selectedSizes ? `${selectedSizes.length} of ${product.sizes_available?.length || 0}` : 'All'}
                        </p>
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => openCustomizeDialog(item)}
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          Customize
                        </Button>
                        {status.design_url && isEnabled && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => generateMockup(product)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Orders */}
      {status.merchandise_enabled && orders.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Orders</CardTitle>
            <CardDescription>
              Merchandise orders from your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {orders.slice(0, 5).map((order) => (
                <div 
                  key={order.order_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">{order.product_name}</p>
                    <p className="text-sm text-slate-500">
                      {order.size} / {order.color} • {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={
                      order.printful_status === 'fulfilled' ? 'text-green-600 border-green-200 bg-green-50' :
                      order.printful_status === 'pending' ? 'text-amber-600 border-amber-200 bg-amber-50' :
                      'text-slate-600 border-slate-200'
                    }>
                      {order.printful_status || 'Processing'}
                    </Badge>
                    <p className="text-sm font-medium text-green-600 mt-1">
                      +${order.org_profit?.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mockup Preview Dialog */}
      <Dialog open={mockupDialogOpen} onOpenChange={setMockupDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Mockup Preview: {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {mockupLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-slate-400 mb-4" />
                <p className="text-slate-600">Generating mockup with your logo...</p>
                <p className="text-sm text-slate-400 mt-1">This may take a few seconds</p>
              </div>
            ) : mockupImages.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {mockupImages.map((mockup, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={mockup.mockup_url || mockup.url || mockup}
                        alt={`Mockup ${idx + 1}`}
                        className="w-full rounded-lg border border-slate-200"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <a 
                          href={mockup.mockup_url || mockup.url || mockup}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white text-sm font-medium"
                        >
                          <ExternalLink className="w-5 h-5" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-500 text-center">
                  These are preview mockups showing how your logo will appear on the product.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                <Shirt className="w-16 h-16 mb-4 text-slate-300" />
                <p>Unable to generate mockup preview</p>
                <p className="text-sm text-slate-400 mt-1">Try again or contact support</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Customization Dialog */}
      <Dialog open={customizeDialogOpen} onOpenChange={setCustomizeDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-500" />
              Customize: {customizingProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          {customizingProduct && (
            <div className="flex-1 overflow-y-auto py-4 space-y-6 pr-2">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <p className="font-medium text-slate-900">Enable Product</p>
                  <p className="text-sm text-slate-500">Show this product during registration</p>
                </div>
                <Switch
                  checked={customizingProduct.customization.enabled}
                  onCheckedChange={(checked) => setCustomizingProduct({
                    ...customizingProduct,
                    customization: { ...customizingProduct.customization, enabled: checked }
                  })}
                />
              </div>

              {/* Colors Selection */}
              {customizingProduct.colors_available?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-slate-700">
                      Available Colors
                      <span className="text-slate-400 font-normal ml-2">
                        ({customizingProduct.customization.selected_colors === null
                          ? 'All selected'
                          : customizingProduct.customization.selected_colors?.length === 0
                            ? 'None selected'
                            : `${customizingProduct.customization.selected_colors.length} selected`})
                      </span>
                    </Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllColors}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        onClick={deselectAllColors}
                        className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {customizingProduct.colors_available.map((color) => {
                      const selected = isColorSelected(color);
                      return (
                        <button
                          key={color}
                          onClick={() => toggleColor(color)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            selected
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {selected && <Check className="w-3 h-3 inline mr-1" />}
                          {color}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Click to toggle. Unselected colors won&apos;t be available for purchase.
                  </p>
                </div>
              )}

              {/* Sizes Selection */}
              {customizingProduct.sizes_available?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-slate-700">
                      Available Sizes
                      <span className="text-slate-400 font-normal ml-2">
                        ({customizingProduct.customization.selected_sizes === null
                          ? 'All selected'
                          : customizingProduct.customization.selected_sizes?.length === 0
                            ? 'None selected'
                            : `${customizingProduct.customization.selected_sizes.length} selected`})
                      </span>
                    </Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllSizes}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-slate-300">|</span>
                      <button
                        type="button"
                        onClick={deselectAllSizes}
                        className="text-xs text-slate-500 hover:text-slate-700 font-medium"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {customizingProduct.sizes_available.map((size) => {
                      const selected = isSizeSelected(size);
                      return (
                        <button
                          key={size}
                          onClick={() => toggleSize(size)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            selected
                              ? 'bg-indigo-600 text-white'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          {selected && <Check className="w-3 h-3 inline mr-1" />}
                          {size}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    Click to toggle. Unselected sizes won&apos;t be available for purchase.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Actions - Fixed at bottom */}
          <div className="flex justify-end gap-3 pt-4 border-t flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => setCustomizeDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveProductCustomization}
              disabled={customizeSaving}
            >
              {customizeSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgMerchandise;
